import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import AALogo from "@/components/AALogo";
import { RefreshCw } from "lucide-react";
import { RECOVERY_QUOTES, duracionDe } from "@/data/recoveryQuotes";
import { useQuieto } from "@/hooks/useQuieto";
import { useSystemStatus } from "@/context/SystemStatusContext";

const PARTICLE_COUNT = 18;

export default function RecoveryScreen() {
  const { retryNow } = useSystemStatus();
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [retrying, setRetrying] = useState(false);
  // ANTES PREGUNTABA SOLO AL NAVEGADOR, así que quien había elegido «Movimiento: reducido»
  // seguía viendo rotar las citas. Ahora manda el ajuste, como en el resto de la casa.
  const reducedMotion = useQuieto();

  // UN RELOJ POR CITA Y NO UN INTERVALO FIJO. Con `setInterval` todas duran lo mismo, y lo que
  // hace falta es que cada una dure lo suyo: el tiempo lo dice `duracionDe`, mirando cuántas
  // palabras tiene. Por eso el efecto depende de `quoteIndex` — al cambiar de cita se arma el
  // siguiente reloj con el tiempo de la cita nueva.
  useEffect(() => {
    if (reducedMotion) return undefined;
    const id = setTimeout(() => {
      setQuoteIndex((i) => (i + 1) % RECOVERY_QUOTES.length);
    }, duracionDe(RECOVERY_QUOTES[quoteIndex]));
    return () => clearTimeout(id);
  }, [reducedMotion, quoteIndex]);

  const particles = useMemo(
    () =>
      Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
        id: i,
        left: `${(i * 137.5) % 100}%`,
        top: `${(i * 71.3) % 100}%`,
        delay: `${(i % 6) * 0.7}s`,
        size: 2 + (i % 3),
      })),
    [],
  );

  const handleRetry = () => {
    setRetrying(true);
    retryNow();
    setTimeout(() => setRetrying(false), 1200);
  };

  const { author, quote } = RECOVERY_QUOTES[quoteIndex];

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="recovery-screen"
      className="fixed inset-0 z-[10000] bg-alpha-black text-white flex items-center justify-center overflow-hidden"
    >
      {!reducedMotion && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {particles.map((p) => (
            <span
              key={p.id}
              className="recovery-particle"
              style={{
                left: p.left,
                top: p.top,
                width: p.size,
                height: p.size,
                animationDelay: p.delay,
              }}
            />
          ))}
        </div>
      )}

      <div className="relative z-10 max-w-lg mx-auto px-6 text-center">
        {/* EL LOGO DE VERDAD, Y NO UNA ALFA ESCRITA. Aquí había una «α» de Cormorant a 7rem,
            que es la cuarta marca distinta que ha tenido esta casa: `AALogo` dice en su propio
            comentario que **esta pantalla** dibujaba antes «un pico suelto con una raya
            debajo». La alfa no era el logo; era la letra que se le parece. Y esta pantalla es
            justo la que más necesita que la marca sea la marca: sale cuando algo va mal, que
            es cuando más falta hace reconocer dónde estás.

            El resplandor pasa de `text-shadow` a `drop-shadow` porque ya no hay texto que
            sombrear: un `text-shadow` sobre un SVG no hace nada y no avisa de que no hace
            nada. El latido y su freno de «reducir movimiento» se quedan como estaban. */}
        <div className={`recovery-alpha ${reducedMotion ? "" : "recovery-alpha-animated"}`} aria-hidden="true">
          <AALogo size={132} variant="light" lockup="icono" />
        </div>

        <div className="mt-8 font-mono-alpha text-[11px] uppercase tracking-widest text-alpha-red">
          Reconectando con Academia Alpha
        </div>
        <p className="mt-4 text-white/70 text-sm">
          Estamos recuperando la conexión con el servidor. Tu sesión y tus datos están a salvo; volveremos en cuanto sea posible.
        </p>

        {/* EL CAMBIO DE CITA SE VE. Antes una frase se sustituía por otra sin más, y a los
            diez o quince segundos nadie está mirando ese trozo de pantalla: la nueva aparecía
            ya empezada y parecía que llevaba ahí todo el rato. Ahora la que se va sube y se
            apaga, y la que llega entra desde abajo creciendo un punto — lo justo para que el
            ojo vuelva, que es para lo que sirve una animación aquí.

            `mode="wait"` Y NO UN CRUCE: dos citas superpuestas medio segundo son dos frases
            distintas leyéndose a la vez, que es exactamente lo que no queremos en una
            pantalla que pide calma. Primero se va una y después llega la otra.

            `initial={false}` PARA LA PRIMERA. Sin eso, la cita inicial también entra
            animándose, y esta pantalla aparece de golpe cuando algo va mal: lo último que
            hace falta es que además se mueva nada más salir.

            EL BOTÓN VA PEGADO A LA CITA, Y NO A UNA CAJA FIJA. Esto tuvo primero una altura
            mínima de 150 px para que el botón no se moviera nunca; lo miró Gabri y la caja
            fija era peor: con una cita de una línea quedaba un agujero de medio palmo y el
            botón se iba al fondo. Lo que se lee bien es **la misma distancia desde la última
            línea**, aunque eso signifique que el botón sube y baja. Él lo pidió así y tiene
            razón: lo que molesta no es que se mueva, es que se mueva SIN MOTIVO.

            Y POR ESO `popLayout` EN VEZ DE `wait`. Sin altura fija, `wait` deja el hueco a
            cero medio segundo y el botón sale disparado hacia arriba y vuelve. `popLayout`
            saca del flujo a la que se va, así que la nueva ya está ocupando su sitio mientras
            la vieja se apaga, y el botón —que lleva `layout`— se DESPLAZA a su altura nueva
            en vez de saltar. El movimiento pasa a ser información: la cita es más corta.

            Y `aria-live="off"` AQUÍ DENTRO. El contenedor es un `role="alert"`, así que sin
            esto un lector de pantalla vuelve a leer la pantalla entera cada vez que rota la
            cita. El aviso se da una vez; las citas acompañan, no avisan. */}
        <div className="mt-9" aria-live="off">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.blockquote
              key={quoteIndex}
              initial={reducedMotion ? false : { opacity: 0, y: 14, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: reducedMotion ? 0.15 : 0.45, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <p className="font-serif-display text-2xl italic leading-snug text-balance">"{quote}"</p>
              <footer className="mt-3 font-mono-alpha text-[10px] uppercase tracking-widest text-white/50">
                — {author}
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <motion.button
          layout={!reducedMotion}
          transition={{ type: "spring", stiffness: 320, damping: 34 }}
          onClick={handleRetry}
          data-testid="recovery-retry-button"
          className="rounded-caja mt-8 inline-flex items-center gap-3 px-8 py-4 bg-alpha-red text-white font-mono-alpha uppercase text-[12px] tracking-widest btn-brutal"
        >
          <RefreshCw className={`w-4 h-4 ${retrying ? "animate-spin" : ""}`} /> Reintentar ahora
        </motion.button>
      </div>
    </div>
  );
}
